'use client';

import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Video, TrendingUp,
  CheckCircle2, Clock, Star, ArrowRight, Play, UserPlus, Bell, ChevronLeft,
  Sparkles, Camera, X, Film, Zap, Trophy, Target, Lock, Eye, EyeOff
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ContractAcceptanceModal from "@/components/modals/contract-acceptance-modal";
import { fetchContractTemplate, acceptContractTemplate } from "@/lib/api/contracts";
import type { ContractTemplate } from "@shared/schema";
import { CANONICAL_PLATFORMS } from "@shared/platforms-mapping";
import { CANONICAL_CONTENT_TYPES } from "@shared/content-types-mapping";
import { CANONICAL_COUNTRIES } from "@shared/countries-mapping";
import { UGC_EXPERIENCE_OPTIONS } from "@shared/ugc-experience-mapping";
import { CANONICAL_PETS } from "@shared/pets-mapping";

// ─── Translations ────────────────────────────────────────────────────────────

const content = {
  fr: {
    hero: {
      badge: "Créateurs UGC",
      title: "Rejoins notre réseau de créateurs",
      subtitle: "Ton profil est ajouté à notre base. On te contacte dès qu'un projet correspond à ton profil et ton pays.",
    },
    steps: [
      { icon: Play, label: "Candidature", desc: "5 min" },
      { icon: UserPlus, label: "Profil ajouté", desc: "Immédiat" },
      { icon: Bell, label: "Contact", desc: "Dès qu'un projet match" },
    ],
    benefits: [
      { icon: TrendingUp, title: "Missions variées", desc: "Des projets adaptés à ton profil" },
      { icon: Star, title: "Marques diverses", desc: "Collabore avec différentes marques" },
      { icon: Clock, title: "Flexibilité totale", desc: "Gère ton temps comme tu veux" },
    ],
    cta: "Commencer ma candidature",
    form: {
      title: "Formulaire de candidature",
      steps: [
        { label: "Qui es-tu ?",   desc: "Infos personnelles & identité" },
        { label: "Ton univers",   desc: "Biographie, spécialités et formats" },
        { label: "Tes outils",    desc: "Plateformes, langues et style de vie" },
        { label: "Ta vitrine",    desc: "Portfolio, tarif et réseaux sociaux" },
      ],
      fields: {
        firstName: "Prénom",
        firstNamePlaceholder: "Marie",
        lastName: "Nom",
        lastNamePlaceholder: "Dupont",
        email: "Email",
        emailPlaceholder: "marie@example.com",
        phone: "Téléphone",
        phonePlaceholder: "+33 6 00 00 00 00",
        birthDate: "Date de naissance",
        gender: "Genre",
        genderHomme: "👨 Homme",
        genderFemme: "👩 Femme",
        genderAutre: "✨ Autre",
        country: "Pays",
        countryPlaceholder: "Sélectionne ton pays",
        location: "Ville",
        locationPlaceholder: "Paris",
        addressPostal: "Adresse postale (optionnel)",
        addressPostalPlaceholder: "12 rue de la Paix, 75001 Paris",
        bio: "Ta biographie",
        bioPlaceholder: "Parle-nous de toi, de ton style de contenu, de ce qui te rend unique...",
        bioMin: "Minimum 50 caractères",
        ugcExperience: "Depuis combien de temps tu fais de l'UGC ?",
        ugcExperiencePlaceholder: "Sélectionne une durée",
        specialties: "Tes spécialités",
        contentTypes: "Tes formats de contenu",
        platforms: "Tes plateformes",
        languages: "Langues parlées",
        withWho: "Avec qui tu peux tourner",
        pets: "Animaux de compagnie (optionnel)",
        equipment: "Ton matériel (optionnel)",
        equipmentPlaceholder: "iPhone 14 Pro, Ring light, Micro Rode NT1...",
        photo: "Photo de profil *",
        photoUploading: "Upload en cours...",
        videoRate: "Tarif vidéo complet *",
        videoRatePlaceholder: "150",
        videoRateSuffix: "€ / vidéo",
        hookRate: "Tarif hook seul *",
        hookRatePlaceholder: "80",
        hookRateSuffix: "€ / hook",
        portfolio: "Lien portfolio / Showreel",
        portfolioPlaceholder: "https://monportfolio.com",
        instagram: "Instagram (optionnel)",
        tiktok: "TikTok (optionnel)",
        password: "Choisis ton mot de passe *",
        confirmPassword: "Confirme ton mot de passe *",
        passwordPlaceholder: "••••••••",
        passwordMismatch: "Les mots de passe ne correspondent pas",
        accountSection: "Ton compte",
        accountSectionSub: "Ces identifiants te permettront de te connecter à ton espace créateur.",
      },
      next: "Étape suivante",
      prev: "Retour",
      submit: "Envoyer ma candidature ✨",
      submitting: "Envoi en cours...",
      errors: {
        emailExists: "Cet email est déjà enregistré dans notre base.",
        generic: "Une erreur est survenue. Veuillez réessayer.",
        uploadFailed: "Impossible d'uploader la photo. Réessaie.",
      },
      emailCheck: {
        hasAccount: "Tu as déjà un compte créateur UGCookie. Connecte-toi pour mettre à jour ton profil.",
        hasAccountLogin: "Me connecter",
        noAccount: "Ta candidature est déjà dans notre base, mais tu n'as pas encore d'espace créateur. Continue pour créer tes accès et mettre à jour ton profil. 💛",
      },
      updateConfirm: {
        message: "Un profil existe déjà avec ce numéro de téléphone. Souhaites-tu continuer et mettre à jour ta fiche ?",
        confirm: "Oui, mettre à jour mon profil",
        cancel: "Annuler",
        noAccountMessage: "Ta candidature est déjà dans notre base, mais tu n'as pas encore d'espace créateur. Crée tes accès maintenant pour mettre à jour ton profil et maximiser tes chances de collabs. 💛",
        noAccountMessagePhone: "Ton numéro est déjà dans notre base, mais tu n'as pas encore d'espace créateur. Crée tes accès maintenant pour mettre à jour ton profil et maximiser tes chances de collabs. 💛",
        noAccountConfirm: "Créer mes accès et mettre à jour mon profil ✨",
        hasAccountMessage: "Tu as déjà un compte créateur UGCookie. Connecte-toi pour mettre à jour ton profil et voir tes opportunités.",
        hasAccountLogin: "Me connecter à mon espace créateur",
      },
      gdpr: {
        label: "J'accepte que mes données personnelles soient collectées et traitées par l'agence dans le cadre de la mise en relation avec des marques.",
        rights: "Conformément au RGPD, vous pouvez exercer vos droits d'accès, de rectification et de suppression en nous contactant.",
        error: "Vous devez accepter la politique de confidentialité pour continuer.",
      },
      prideVideo: {
        title: "Ta vidéo phare (optionnel)",
        desc: "Partage la vidéo dont tu es le/la plus fier(e) — elle sera visible sur ton profil UGCookie.",
        consentLabel: "J'autorise UGCookie à afficher cette vidéo sur son site internet, dans ses supports marketing et commerciaux, et à la présenter à des clients potentiels. Cette autorisation est révocable à tout moment en contactant l'équipe UGCookie.",
        uploadBtn: "Choisir une vidéo",
        uploadHint: "MP4, MOV, WebM · max 200 Mo",
        uploading: "Upload en cours…",
        consentRequired: "Accepte l'autorisation ci-dessus pour uploader",
        remove: "Supprimer",
        categoryLabel: "Catégorie de ta vidéo",
        categoryHint: "Aide-nous à bien présenter ton contenu aux marques",
      },
      stepBanners: [
        { emoji: "👋", title: "Présente-toi !", sub: "Dis-nous qui tu es — ça prend 2 minutes." },
        { emoji: "🎨", title: "Ton univers créatif", sub: "Montre ta personnalité et tes spécialités." },
        { emoji: "🛠️", title: "Tes super-pouvoirs", sub: "Plateformes, langues, équipement — tout compte !" },
        { emoji: "🌟", title: "Ta vitrine finale", sub: "Photo, portfolio, tarif — la touche finale !" },
      ],
    },
    success: {
      title: "Candidature envoyée !",
      message: "Merci pour ta candidature. Ton profil a été ajouté à notre base. On te contactera dès qu'un projet correspond à ton profil.",
      back: "Retour à l'accueil",
    },
    langLabel: "Langue / Language",
  },
  en: {
    hero: {
      badge: "UGC Creators",
      title: "Join our creator network",
      subtitle: "Your profile is added to our database. We'll contact you as soon as a project matches your profile and country.",
    },
    steps: [
      { icon: Play, label: "Apply", desc: "5 min" },
      { icon: UserPlus, label: "Profile added", desc: "Immediate" },
      { icon: Bell, label: "Contact", desc: "When a project matches" },
    ],
    benefits: [
      { icon: TrendingUp, title: "Varied missions", desc: "Projects tailored to your profile" },
      { icon: Star, title: "Diverse brands", desc: "Collaborate with different brands" },
      { icon: Clock, title: "Total flexibility", desc: "Manage your time as you wish" },
    ],
    cta: "Start my application",
    form: {
      title: "Application Form",
      steps: [
        { label: "Who are you?",    desc: "Personal info & identity" },
        { label: "Your world",      desc: "Bio, specialties and formats" },
        { label: "Your tools",      desc: "Platforms, languages and lifestyle" },
        { label: "Your showcase",   desc: "Portfolio, rate and social media" },
      ],
      fields: {
        firstName: "First name",
        firstNamePlaceholder: "Jane",
        lastName: "Last name",
        lastNamePlaceholder: "Smith",
        email: "Email",
        emailPlaceholder: "jane@example.com",
        phone: "Phone",
        phonePlaceholder: "+44 7000 000000",
        birthDate: "Date of birth",
        gender: "Gender",
        genderHomme: "👨 Male",
        genderFemme: "👩 Female",
        genderAutre: "✨ Other",
        country: "Country",
        countryPlaceholder: "Select your country",
        location: "City",
        locationPlaceholder: "London",
        addressPostal: "Postal address (optional)",
        addressPostalPlaceholder: "12 High Street, London, SW1A 1AA",
        bio: "Your bio",
        bioPlaceholder: "Tell us about yourself, your content style, what makes you unique...",
        bioMin: "Minimum 50 characters",
        specialties: "Your specialties",
        ugcExperience: "How long have you been doing UGC?",
        ugcExperiencePlaceholder: "Select a duration",
        contentTypes: "Your content formats",
        platforms: "Your platforms",
        languages: "Languages spoken",
        withWho: "Who you can film with",
        pets: "Pets (optional)",
        equipment: "Your equipment (optional)",
        equipmentPlaceholder: "iPhone 14 Pro, Ring light, Rode NT1 mic...",
        photo: "Profile photo *",
        photoUploading: "Uploading...",
        videoRate: "Full video rate *",
        videoRatePlaceholder: "150",
        videoRateSuffix: "€ / video",
        hookRate: "Hook rate *",
        hookRatePlaceholder: "80",
        hookRateSuffix: "€ / hook",
        portfolio: "Portfolio / Showreel link",
        portfolioPlaceholder: "https://myportfolio.com",
        instagram: "Instagram (optional)",
        tiktok: "TikTok (optional)",
        password: "Choose your password *",
        confirmPassword: "Confirm your password *",
        passwordPlaceholder: "••••••••",
        passwordMismatch: "Passwords don't match",
        accountSection: "Your account",
        accountSectionSub: "These credentials will allow you to log in to your creator portal.",
      },
      next: "Next step",
      prev: "Back",
      submit: "Submit my application ✨",
      submitting: "Submitting...",
      errors: {
        emailExists: "This email is already registered in our database.",
        generic: "An error occurred. Please try again.",
        uploadFailed: "Could not upload photo. Please try again.",
      },
      emailCheck: {
        hasAccount: "You already have a UGCookie creator account. Log in to update your profile.",
        hasAccountLogin: "Log in",
        noAccount: "Your application is already in our database, but you don't have a creator space yet. Continue to create your access and update your profile. 💛",
      },
      updateConfirm: {
        message: "A profile already exists with this phone number. Do you want to continue and update your profile?",
        confirm: "Yes, update my profile",
        cancel: "Cancel",
        noAccountMessage: "Your application is already in our database, but you don't have a creator space yet. Create your access now to update your profile and maximise your chances of getting collabs. 💛",
        noAccountMessagePhone: "Your phone number is already in our database, but you don't have a creator space yet. Create your access now to update your profile and maximise your chances of getting collabs. 💛",
        noAccountConfirm: "Create my access and update my profile ✨",
        hasAccountMessage: "You already have a UGCookie creator account. Log in to update your profile and see your opportunities.",
        hasAccountLogin: "Log in to my creator space",
      },
      gdpr: {
        label: "I agree that my personal data will be collected and processed by the agency for the purpose of connecting me with brands.",
        rights: "In accordance with GDPR, you may exercise your rights of access, rectification and deletion by contacting us.",
        error: "You must accept the privacy policy to continue.",
      },
      prideVideo: {
        title: "Your proudest video (optional)",
        desc: "Share the video you're most proud of — it will be visible on your UGCookie profile.",
        consentLabel: "I authorize UGCookie to display this video on its website, in its marketing and commercial materials, and to present it to potential clients. This authorization can be revoked at any time by contacting the UGCookie team.",
        uploadBtn: "Choose a video",
        uploadHint: "MP4, MOV, WebM · max 200 MB",
        uploading: "Uploading…",
        consentRequired: "Accept the authorization above to upload",
        remove: "Remove",
        categoryLabel: "Video category",
        categoryHint: "Helps us present your content to the right brands",
      },
      stepBanners: [
        { emoji: "👋", title: "Introduce yourself!", sub: "Tell us who you are — takes 2 minutes." },
        { emoji: "🎨", title: "Your creative world", sub: "Show your personality and specialties." },
        { emoji: "🛠️", title: "Your superpowers", sub: "Platforms, languages, equipment — it all counts!" },
        { emoji: "🌟", title: "Your final showcase", sub: "Photo, portfolio, rate — the finishing touch!" },
      ],
    },
    success: {
      title: "Application submitted!",
      message: "Thank you for applying. Your profile has been added to our database. We'll contact you as soon as a project matches your profile.",
      back: "Back to home",
    },
    langLabel: "Langue / Language",
  },
};

// ─── Constants ────────────────────────────────────────────────────────────────

interface PillItem {
  label: string;
  display: string;
}

// Sources canoniques partagées (@shared/*-mapping) — évite que cette page dérive
// de la liste utilisée par le panneau de filtres et le formulaire d'édition admin.
const CONTENT_TYPES: PillItem[] = CANONICAL_CONTENT_TYPES.map(c => ({ label: c.id, display: c.label }));

const PETS: PillItem[] = CANONICAL_PETS.map(p => ({ label: p.id, display: p.label }));

const COUNTRIES = CANONICAL_COUNTRIES;

const SPECIALTIES: { fr: PillItem; en: PillItem }[] = [
  { fr: { label: "Beauté",         display: "💄 Beauté"          }, en: { label: "Beauty",       display: "💄 Beauty"       } },
  { fr: { label: "Mode",           display: "👗 Mode"             }, en: { label: "Fashion",      display: "👗 Fashion"      } },
  { fr: { label: "Fitness / Sport", display: "💪 Fitness / Sport" }, en: { label: "Fitness / Sport", display: "💪 Fitness / Sport" } },
  { fr: { label: "Food",           display: "🍕 Food"             }, en: { label: "Food",         display: "🍕 Food"         } },
  { fr: { label: "Tech",           display: "💻 Tech"             }, en: { label: "Tech",         display: "💻 Tech"         } },
  { fr: { label: "Gaming",         display: "🎮 Gaming"           }, en: { label: "Gaming",       display: "🎮 Gaming"       } },
  { fr: { label: "Voyage",         display: "✈️ Voyage"           }, en: { label: "Travel",       display: "✈️ Travel"       } },
  { fr: { label: "Lifestyle",      display: "✨ Lifestyle"         }, en: { label: "Lifestyle",    display: "✨ Lifestyle"    } },
  { fr: { label: "Maison",         display: "🏠 Maison"           }, en: { label: "Home & Decor", display: "🏠 Home & Decor" } },
  { fr: { label: "Famille",        display: "👨‍👩‍👧 Famille"        }, en: { label: "Family",       display: "👨‍👩‍👧 Family"       } },
  { fr: { label: "Éducation",      display: "📚 Éducation"        }, en: { label: "Education",    display: "📚 Education"    } },
  { fr: { label: "Maman",          display: "👶 Maman"            }, en: { label: "Mom",          display: "👶 Mom"          } },
  { fr: { label: "Loisirs",        display: "🎯 Loisirs"          }, en: { label: "Leisure",      display: "🎯 Leisure"      } },
  { fr: { label: "BtoB",           display: "🏢 BtoB"             }, en: { label: "B2B",          display: "🏢 B2B"          } },
];


// Catégories pour la vidéo phare — valeurs normalisées (slugs) identiques au portail créateur.
// Stocker la valeur (slug) et non le label permet d'afficher correctement dans le portail.
const PRIDE_CATEGORIES: Array<{ value: string; display: string }> = [
  { value: 'beauté',         display: '💄 Beauté' },
  { value: 'mode',           display: '👗 Mode' },
  { value: 'sport',          display: '💪 Fitness / Sport' },
  { value: 'alimentation',   display: '🍕 Food' },
  { value: 'technologie',    display: '💻 Tech' },
  { value: 'gaming',         display: '🎮 Gaming' },
  { value: 'voyage',         display: '✈️ Voyage' },
  { value: 'lifestyle',      display: '✨ Lifestyle' },
  { value: 'maison',         display: '🏠 Maison' },
  { value: 'famille',        display: '👨‍👩‍👧 Famille' },
  { value: 'éducation',      display: '📚 Éducation' },
  { value: 'parentalité',    display: '👶 Maman' },
  { value: 'divertissement', display: '🎯 Loisirs' },
  { value: 'business',       display: '🏢 BtoB' },
];

const WITH_WHO: { fr: PillItem; en: PillItem }[] = [
  { fr: { label: "En couple",  display: "💑 En couple"      }, en: { label: "As a couple",  display: "💑 As a couple"  } },
  { fr: { label: "En famille", display: "👨‍👩‍👧 En famille" }, en: { label: "With family",  display: "👨‍👩‍👧 With family" } },
  { fr: { label: "Entre amis", display: "👫 Entre amis"     }, en: { label: "With friends", display: "👫 With friends" } },
  { fr: { label: "Solo",       display: "🙋 Solo"           }, en: { label: "Solo",         display: "🙋 Solo"         } },
];

const PLATFORMS: { fr: PillItem; en: PillItem }[] = CANONICAL_PLATFORMS.map(p => {
  const item = { label: p.id, display: p.label };
  return { fr: item, en: item };
});

const LANGUAGES: { fr: PillItem; en: PillItem }[] = [
  { fr: { label: "Français",    display: "🇫🇷 Français"    }, en: { label: "French",     display: "🇫🇷 French"     } },
  { fr: { label: "Anglais",     display: "🇬🇧 Anglais"     }, en: { label: "English",    display: "🇬🇧 English"    } },
  { fr: { label: "Espagnol",    display: "🇪🇸 Espagnol"    }, en: { label: "Spanish",    display: "🇪🇸 Spanish"    } },
  { fr: { label: "Allemand",    display: "🇩🇪 Allemand"    }, en: { label: "German",     display: "🇩🇪 German"     } },
  { fr: { label: "Italien",     display: "🇮🇹 Italien"     }, en: { label: "Italian",    display: "🇮🇹 Italian"    } },
  { fr: { label: "Portugais",   display: "🇵🇹 Portugais"   }, en: { label: "Portuguese", display: "🇵🇹 Portuguese" } },
  { fr: { label: "Arabe",       display: "🌙 Arabe"        }, en: { label: "Arabic",     display: "🌙 Arabic"      } },
  { fr: { label: "Néerlandais", display: "🇳🇱 Néerlandais" }, en: { label: "Dutch",      display: "🇳🇱 Dutch"      } },
];

// ─── Schema ───────────────────────────────────────────────────────────────────

const applySchema = z.object({
  firstName: z.string().min(2, 'Minimum 2 caractères'),
  lastName: z.string().min(2, 'Minimum 2 caractères'),
  email: z.string().email('Email invalide'),
  phone: z.string().min(1, 'Téléphone requis'),
  birthDate: z.string().min(1, 'Date de naissance requise'),
  gender: z.enum(['homme', 'femme', 'autre']),
  country: z.string().min(1, 'Pays requis'),
  location: z.string().min(1, 'Ville requise'),
  addressPostal: z.string().optional(),
  bio: z.string().min(50, 'Minimum 50 caractères'),
  specialties: z.array(z.string()).min(1, 'Sélectionne au moins une spécialité'),
  contentTypes: z.array(z.string()).min(1, 'Sélectionne au moins un format de contenu'),
  platforms: z.array(z.string()).min(1, 'Sélectionne au moins une plateforme'),
  languages: z.array(z.string()).min(1, 'Sélectionne au moins une langue'),
  ugcExperience: z.string().optional(),
  pets: z.array(z.string()).optional(),
  equipment: z.string().optional(),
  photoUrl: z.string().min(1, 'Photo de profil requise'),
  videoRate: z.coerce.number().min(1, 'Tarif requis (minimum 1€)'),
  hookRate: z.coerce.number().min(1, 'Tarif hook requis (minimum 1€)'),
  website: z.string().min(1, 'Portfolio requis').url('URL invalide (ex: https://monportfolio.com)'),
  instagram: z.string().optional(),
  tiktok: z.string().optional(),
  password: z.string().min(8, 'Minimum 8 caractères'),
  confirmPassword: z.string().min(1, 'Confirmation requise'),
  gdprConsent: z.boolean().refine(v => v === true, 'Vous devez accepter la politique de confidentialité'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

type ApplyFormData = z.infer<typeof applySchema>;

// ─── Language Switcher ────────────────────────────────────────────────────────

function LanguageSwitcher({ language, onChange }: { language: 'fr' | 'en'; onChange: (l: 'fr' | 'en') => void }) {
  return (
    <div className="flex items-center gap-1 p-1 rounded-xl bg-muted w-fit">
      <button
        onClick={() => onChange('fr')}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          language === 'fr'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <span className="text-base">🇫🇷</span>
        Français
      </button>
      <button
        onClick={() => onChange('en')}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          language === 'en'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <span className="text-base">🇬🇧</span>
        English
      </button>
    </div>
  );
}

// ─── PillGroup (multi-select) ─────────────────────────────────────────────────

function PillGroup({
  items,
  value,
  onChange,
}: {
  items: PillItem[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (label: string) => {
    onChange(value.includes(label) ? value.filter(x => x !== label) : [...value, label]);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const selected = value.includes(item.label);
        return (
          <button
            key={item.label}
            type="button"
            onClick={() => toggle(item.label)}
            className={cn(
              "px-3.5 py-2 rounded-full text-sm font-medium border-2 transition-all duration-150 select-none",
              selected
                ? "bg-primary text-primary-foreground border-primary shadow-md scale-[1.06] -translate-y-0.5"
                : "bg-background text-foreground border-border hover:border-primary/60 hover:bg-primary/5 hover:scale-[1.02] active:scale-95"
            )}
          >
            {value.includes(item.label) && <span className="text-[10px] mr-0.5">✓</span>}
            {item.display}
          </button>
        );
      })}
    </div>
  );
}

// ─── GenderPills (single-select) ──────────────────────────────────────────────

function GenderPills({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "px-5 py-2.5 rounded-full text-sm font-semibold border-2 transition-all duration-150 select-none",
              selected
                ? "bg-primary text-primary-foreground border-primary shadow-md scale-[1.03]"
                : "bg-background text-foreground border-border hover:border-primary/60 hover:bg-primary/5 active:scale-95"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Bio character counter ────────────────────────────────────────────────────

function BioCounter({ value, min = 50 }: { value: string; min?: number }) {
  const len = value?.length ?? 0;
  const pct = Math.min((len / min) * 100, 100);
  const reached = len >= min;
  const color = pct < 40 ? 'bg-red-400' : pct < 80 ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="mt-1.5 space-y-1">
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-300 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between">
        <span className={cn("text-xs font-medium transition-colors", reached ? "text-emerald-600" : "text-muted-foreground")}>
          {reached ? "✓ Super !" : `${len} / ${min} min`}
        </span>
        {!reached && <span className="text-xs text-muted-foreground">{min - len} restants</span>}
      </div>
    </div>
  );
}

// ─── Stepper Progress Bar ─────────────────────────────────────────────────────

function StepperBar({ step, steps }: { step: number; steps: { label: string; desc: string }[] }) {
  const total = steps.length;
  const pct = Math.round(((step - 1) / total) * 100);
  return (
    <div className="mb-6">
      {/* XP bar */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-primary flex items-center gap-1">
          <Zap className="h-3 w-3" /> Étape {step}/{total}
        </span>
        <span className="text-xs font-bold text-primary">{pct}%</span>
      </div>
      <div className="h-2.5 bg-muted rounded-full overflow-hidden mb-5">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out bg-gradient-to-r from-violet-500 via-primary to-indigo-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Step circles */}
      <div className="flex items-center mb-4">
        {steps.map((s, i) => {
          const num = i + 1;
          const isActive = num === step;
          const isDone = num < step;
          return (
            <div key={i} className="flex items-center flex-1 min-w-0">
              <div className="flex flex-col items-center shrink-0">
                <div
                  className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300",
                    isDone
                      ? "bg-primary border-primary text-primary-foreground shadow-md scale-95"
                      : isActive
                      ? "bg-primary/10 border-primary text-primary ring-4 ring-primary/20 scale-110"
                      : "bg-background border-muted-foreground/30 text-muted-foreground/50"
                  )}
                >
                  {isDone ? <CheckCircle2 className="h-4 w-4" /> : num}
                </div>
                <span
                  className={cn(
                    "text-[10px] mt-1 text-center hidden sm:block max-w-[68px] leading-tight font-medium",
                    isActive ? "text-primary" : isDone ? "text-foreground/70" : "text-muted-foreground/40"
                  )}
                >
                  {s.label}
                </span>
              </div>
              {i < total - 1 && (
                <div className="h-0.5 flex-1 mx-2 rounded-full overflow-hidden bg-muted-foreground/20">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-700"
                    style={{ width: isDone ? '100%' : '0%' }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── BirthDatePicker ──────────────────────────────────────────────────────────

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function BirthDatePicker({
  value,
  onChange,
  language,
  error,
}: {
  value: string;
  onChange: (date: string) => void;
  language: 'fr' | 'en';
  error?: boolean;
}) {
  const months = language === 'fr' ? MONTHS_FR : MONTHS_EN;

  // Seed local state from initial value (YYYY-MM-DD), then manage independently
  const parsed = value ? value.split('-') : [];
  const [year,  setYear]  = useState(parsed[0] ?? '');
  const [month, setMonth] = useState(parsed[1] ?? '');
  const [day,   setDay]   = useState(parsed[2] ?? '');

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 80;
  const maxYear = currentYear - 16;

  const daysInMonth = year && month
    ? new Date(Number(year), Number(month), 0).getDate()
    : 31;

  const notify = (y: string, m: string, d: string) => {
    if (y && m && d) {
      const maxDay = new Date(Number(y), Number(m), 0).getDate();
      const clampedDay = Math.min(Number(d), maxDay).toString().padStart(2, '0');
      onChange(`${y}-${m.padStart(2, '0')}-${clampedDay}`);
    } else {
      onChange('');
    }
  };

  const handleDay = (v: string) => {
    setDay(v);
    notify(year, month, v);
  };

  const handleMonth = (v: string) => {
    setMonth(v);
    // Clamp day if needed
    const maxDay = year ? new Date(Number(year), Number(v), 0).getDate() : 31;
    const newDay = day && Number(day) > maxDay ? maxDay.toString().padStart(2, '0') : day;
    if (newDay !== day) setDay(newDay);
    notify(year, v, newDay);
  };

  const handleYear = (v: string) => {
    setYear(v);
    notify(v, month, day);
  };

  const borderClass = error ? 'border-destructive' : '';

  return (
    <div className="grid grid-cols-3 gap-2">
      {/* Day */}
      <Select value={day} onValueChange={handleDay}>
        <SelectTrigger className={borderClass}>
          <SelectValue placeholder={language === 'fr' ? 'Jour' : 'Day'} />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: daysInMonth }, (_, i) => {
            const v = (i + 1).toString().padStart(2, '0');
            return <SelectItem key={v} value={v}>{i + 1}</SelectItem>;
          })}
        </SelectContent>
      </Select>

      {/* Month */}
      <Select value={month} onValueChange={handleMonth}>
        <SelectTrigger className={borderClass}>
          <SelectValue placeholder={language === 'fr' ? 'Mois' : 'Month'} />
        </SelectTrigger>
        <SelectContent>
          {months.map((label, i) => {
            const v = (i + 1).toString().padStart(2, '0');
            return <SelectItem key={v} value={v}>{label}</SelectItem>;
          })}
        </SelectContent>
      </Select>

      {/* Year */}
      <Select value={year} onValueChange={handleYear}>
        <SelectTrigger className={borderClass}>
          <SelectValue placeholder={language === 'fr' ? 'Année' : 'Year'} />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
            const y = (maxYear - i).toString();
            return <SelectItem key={y} value={y}>{y}</SelectItem>;
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─── Application Form (stepper) ───────────────────────────────────────────────

function ApplicationForm({
  language,
  onSuccess,
}: {
  language: 'fr' | 'en';
  onSuccess: (email: string) => void;
}) {
  const t = content[language].form;
  const [step, setStep] = useState(1);
  const [serverError, setServerError] = useState<string | null>(null);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<Record<string, unknown> | null>(null);
  const [existingHasAccount, setExistingHasAccount] = useState<boolean | null>(null);
  const [pendingDuplicateType, setPendingDuplicateType] = useState<'email' | 'phone' | null>(null);
  const [emailCheckStatus, setEmailCheckStatus] = useState<'idle' | 'checking' | 'has-account' | 'no-account' | 'free'>('idle');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emailDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCheckedEmailRef = useRef<string>('');
  const checkAbortRef = useRef<AbortController | null>(null);
  const [prideVideoUrl, setPrideVideoUrl] = useState('');
  const [prideVideoConsent, setPrideVideoConsent] = useState(false);
  const [prideVideoUploading, setPrideVideoUploading] = useState(false);
  const [prideVideoCategory, setPrideVideoCategory] = useState('');
  const [prideVideoCategoryError, setPrideVideoCategoryError] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [celebrating, setCelebrating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    clearErrors,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<ApplyFormData>({
    resolver: zodResolver(applySchema) as Resolver<ApplyFormData>,
    defaultValues: {
      specialties: [],
      contentTypes: [],
      platforms: [],
      languages: [],
      pets: [],
      equipment: '',
      website: '',
      videoRate: undefined,
      hookRate: undefined,
    },
  });

  const specialties  = watch('specialties')  ?? [];
  const contentTypes = watch('contentTypes') ?? [];
  const platforms    = watch('platforms')    ?? [];
  const langs        = watch('languages')    ?? [];
  const pets         = watch('pets')         ?? [];
  const country      = watch('country');
  const gender       = watch('gender');
  const bioValue     = watch('bio') ?? '';

  const genderOptions = [
    { value: 'homme', label: t.fields.genderHomme },
    { value: 'femme', label: t.fields.genderFemme },
    { value: 'autre', label: t.fields.genderAutre },
  ];

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoUploading(true);
    setServerError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/creators/upload-avatar', { method: 'POST', body: fd });
      if (res.ok) {
        const { url } = await res.json();
        setPhotoUrl(url);
        setValue('photoUrl', url, { shouldValidate: true });
      } else {
        setServerError(t.errors.uploadFailed);
      }
    } catch {
      setServerError(t.errors.uploadFailed);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPrideVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/creators/upload-video', { method: 'POST', body: fd });
      if (res.ok) {
        const { url } = await res.json() as { url: string };
        setPrideVideoUrl(url);
      }
    } catch { /* ignore */ }
    finally { setPrideVideoUploading(false); if (videoInputRef.current) videoInputRef.current.value = ''; }
  };

  const goNext = async () => {
    let valid = false;
    if (step === 1) {
      valid = await trigger(['firstName', 'lastName', 'email', 'phone', 'birthDate', 'gender', 'country', 'location']);
    } else if (step === 2) {
      valid = await trigger(['bio', 'specialties', 'contentTypes']);
    } else if (step === 3) {
      valid = await trigger(['platforms', 'languages']);
    } else {
      valid = true;
    }
    if (valid) {
      if (step === 3) {
        clearErrors(['photoUrl', 'videoRate', 'hookRate', 'website', 'gdprConsent']);
      }
      setCelebrating(true);
      setTimeout(() => {
        setCelebrating(false);
        setStep(s => s + 1);
      }, 700);
    }
  };

  const goPrev = () => setStep(s => s - 1);

  const buildPayload = (data: ApplyFormData, forceUpdate = false): Record<string, unknown> => {
    const socialLinks: Record<string, string> = {};
    if (data.instagram) socialLinks.instagram = data.instagram;
    if (data.tiktok)    socialLinks.tiktok = data.tiktok;
    return {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      birthDate: data.birthDate,
      gender: data.gender,
      country: data.country,
      location: data.location,
      addressPostal: data.addressPostal,
      bio: data.bio,
      specialties: data.specialties,
      contentTypes: data.contentTypes,
      platforms: data.platforms,
      languages: data.languages,
      ugcExperience: data.ugcExperience,
      pets: data.pets,
      equipment: data.equipment,
      photoUrl: photoUrl ?? undefined,
      videoRate: data.videoRate,
      hookRate: data.hookRate,
      website: data.website,
      socialLinks,
      applicationLanguage: language,
      forceUpdate,
      prideVideoUrl: prideVideoUrl || null,
      prideVideoConsent,
      prideVideoCategory: prideVideoCategory || null,
      password: data.password,
    };
  };

  const sendApplication = async (payload: Record<string, unknown>) => {
    const res = await fetch('/api/creators/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return { res, json: await res.json() };
  };

  const checkEmailOnBlur = async (email: string) => {
    if (!email || !email.includes('@')) return;
    if (email === lastCheckedEmailRef.current) return;
    if (checkAbortRef.current) checkAbortRef.current.abort();
    checkAbortRef.current = new AbortController();
    setEmailCheckStatus('checking');
    try {
      const res = await fetch(`/api/creators/check-email?email=${encodeURIComponent(email)}`, {
        signal: checkAbortRef.current.signal,
      });
      const json = await res.json() as { exists: boolean; hasAccount: boolean };
      lastCheckedEmailRef.current = email;
      if (!json.exists) {
        setEmailCheckStatus('free');
      } else {
        setEmailCheckStatus(json.hasAccount ? 'has-account' : 'no-account');
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setEmailCheckStatus('idle');
    }
  };

  const onSubmit = async (data: ApplyFormData) => {
    setServerError(null);
    setPendingUpdatePayload(null);
    if (prideVideoUrl && !prideVideoCategory) {
      setPrideVideoCategoryError(true);
      return;
    }
    setPrideVideoCategoryError(false);
    try {
      const payload = buildPayload(data);
      const { res, json } = await sendApplication(payload);

      if (!res.ok) {
        if (json.error === 'EMAIL_EXISTS') {
          setPendingUpdatePayload(buildPayload(data, true));
          setExistingHasAccount(json.hasAccount ?? false);
          setPendingDuplicateType('email');
        } else if (json.error === 'PHONE_EXISTS') {
          setPendingUpdatePayload(buildPayload(data, true));
          setExistingHasAccount(json.hasAccount ?? false);
          setPendingDuplicateType('phone');
        } else {
          setServerError(t.errors.generic);
        }
        return;
      }

      onSuccess(data.email);
    } catch {
      setServerError(t.errors.generic);
    }
  };

  const confirmUpdate = async () => {
    if (!pendingUpdatePayload) return;
    setServerError(null);
    try {
      const { res } = await sendApplication(pendingUpdatePayload);
      if (!res.ok) {
        setServerError(t.errors.generic);
        setPendingUpdatePayload(null);
        return;
      }
      setPendingUpdatePayload(null);
      onSuccess((pendingUpdatePayload.email as string) ?? '');
    } catch {
      setServerError(t.errors.generic);
      setPendingUpdatePayload(null);
    }
  };

  const banner = t.stepBanners[step - 1];

  return (
    <div className="relative">
      <style>{`
        @keyframes autofillEmailDetect { from {} to {} }
        @keyframes autofillPhoneDetect { from {} to {} }
        input.autofill-email-watch:-webkit-autofill {
          animation-name: autofillEmailDetect;
          animation-duration: 0.001s;
        }
        input.autofill-phone-watch:-webkit-autofill {
          animation-name: autofillPhoneDetect;
          animation-duration: 0.001s;
        }
      `}</style>
      {/* Level-up flash overlay */}
      {celebrating && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm rounded-xl pointer-events-none">
          <div className="text-5xl animate-bounce mb-2">⭐</div>
          <p className="text-lg font-bold text-primary animate-pulse">Super !</p>
        </div>
      )}

      <StepperBar step={step} steps={t.steps} />

      {/* Step banner */}
      <div className="mb-5 rounded-xl bg-gradient-to-r from-primary/5 to-violet-500/5 border border-primary/10 px-4 py-3 flex items-center gap-3">
        <span className="text-2xl leading-none">{banner.emoji}</span>
        <div>
          <p className="font-bold text-sm leading-tight">{banner.title}</p>
          <p className="text-xs text-muted-foreground">{banner.sub}</p>
        </div>
      </div>

      {step === 1 && (
        <p className="text-xs text-muted-foreground text-center mb-4">
          Déjà ton accès ?{' '}
          <a href="/creator-portal/login" className="underline underline-offset-2 hover:text-foreground transition-colors">
            Connecte-toi
          </a>
        </p>
      )}

      <form onSubmit={(e) => e.preventDefault()}>
        {/* ── Step 1 — Qui es-tu ? ─────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">{t.fields.firstName} *</Label>
                <Input id="firstName" placeholder={t.fields.firstNamePlaceholder} {...register('firstName')} />
                {errors.firstName && <p className="text-xs text-destructive">{errors.firstName.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">{t.fields.lastName} *</Label>
                <Input id="lastName" placeholder={t.fields.lastNamePlaceholder} {...register('lastName')} />
                {errors.lastName && <p className="text-xs text-destructive">{errors.lastName.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">{t.fields.email} *</Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t.fields.emailPlaceholder}
                    className="autofill-email-watch"
                    {...register('email')}
                    onChange={(e) => {
                      register('email').onChange(e);
                      lastCheckedEmailRef.current = '';
                      setEmailCheckStatus('idle');
                      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
                      const val = e.target.value;
                      emailDebounceRef.current = setTimeout(() => {
                        emailDebounceRef.current = null;
                        void checkEmailOnBlur(val);
                      }, 700);
                    }}
                    onBlur={(e) => {
                      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
                      void checkEmailOnBlur(e.target.value);
                    }}
                    onAnimationStart={(e) => {
                      if (e.animationName === 'autofillEmailDetect') {
                        setTimeout(() => {
                          const val = (e.target as HTMLInputElement).value;
                          if (val) {
                            setValue('email', val, { shouldDirty: true });
                            void checkEmailOnBlur(val);
                          }
                        }, 50);
                      }
                    }}
                  />
                  {emailCheckStatus === 'checking' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground animate-pulse">…</span>
                  )}
                </div>
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
                {/* Feedback inline dès l'étape 1 */}
                {emailCheckStatus === 'has-account' && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">👋</span>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200">{t.emailCheck.hasAccount}</p>
                    </div>
                    <a
                      href="/creator-portal/login"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300 underline underline-offset-2"
                    >
                      {t.emailCheck.hasAccountLogin} →
                    </a>
                  </div>
                )}
                {emailCheckStatus === 'no-account' && (
                  <div className="rounded-xl border border-violet-200 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/30 p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">✨</span>
                      <p className="text-sm font-medium text-violet-900 dark:text-violet-200">{t.emailCheck.noAccount}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">{t.fields.phone} *</Label>
                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  autoCorrect="off"
                  placeholder={t.fields.phonePlaceholder}
                  className="autofill-phone-watch"
                  {...register('phone')}
                  onChange={(e) => {
                    register('phone').onChange(e);
                    setValue('phone', e.target.value, { shouldDirty: true });
                  }}
                  onAnimationStart={(e) => {
                    if (e.animationName === 'autofillPhoneDetect') {
                      setTimeout(() => {
                        const val = (e.target as HTMLInputElement).value;
                        if (val) setValue('phone', val, { shouldDirty: true });
                      }, 50);
                    }
                  }}
                />
                {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t.fields.birthDate} *</Label>
                <BirthDatePicker
                  value={watch('birthDate') ?? ''}
                  onChange={(v) => setValue('birthDate', v, { shouldValidate: v !== '' })}
                  language={language}
                  error={!!errors.birthDate}
                />
                {errors.birthDate && <p className="text-xs text-destructive">{errors.birthDate.message}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t.fields.gender} *</Label>
              <GenderPills
                options={genderOptions}
                value={gender ?? ''}
                onChange={(v) => setValue('gender', v as 'homme' | 'femme' | 'autre', { shouldValidate: true })}
              />
              {errors.gender && <p className="text-xs text-destructive">{errors.gender.message}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="country">{t.fields.country} *</Label>
                <Select
                  value={country ?? ''}
                  onValueChange={(v) => setValue('country', v, { shouldValidate: true })}
                >
                  <SelectTrigger id="country">
                    <SelectValue placeholder={t.fields.countryPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.country && <p className="text-xs text-destructive">{errors.country.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="location">{t.fields.location} *</Label>
                <Input id="location" placeholder={t.fields.locationPlaceholder} {...register('location')} />
                {errors.location && <p className="text-xs text-destructive">{errors.location.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="addressPostal">{t.fields.addressPostal}</Label>
                <Input id="addressPostal" placeholder={t.fields.addressPostalPlaceholder} {...register('addressPostal')} />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2 — Ton univers ──────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-200">
            <div className="space-y-1.5">
              <Label htmlFor="bio">{t.fields.bio} *</Label>
              <Textarea id="bio" placeholder={t.fields.bioPlaceholder} rows={4} {...register('bio')} />
              {errors.bio
                ? <p className="text-xs text-destructive">{errors.bio.message}</p>
                : <BioCounter value={bioValue} />
              }
            </div>
            <div className="space-y-1.5">
              <Label>{t.fields.ugcExperience}</Label>
              <Select
                value={watch('ugcExperience') ?? ''}
                onValueChange={(v) => setValue('ugcExperience', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.fields.ugcExperiencePlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {UGC_EXPERIENCE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt[language]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2.5">
              <Label className="text-sm font-semibold">{t.fields.specialties} *</Label>
              <PillGroup items={SPECIALTIES.map(s => s[language])} value={specialties} onChange={(v) => setValue('specialties', v, { shouldValidate: true })} />
              {errors.specialties && <p className="text-xs text-destructive">{errors.specialties.message}</p>}
            </div>
            <div className="space-y-2.5">
              <Label className="text-sm font-semibold">{t.fields.contentTypes} *</Label>
              <PillGroup items={CONTENT_TYPES} value={contentTypes} onChange={(v) => setValue('contentTypes', v, { shouldValidate: true })} />
              {errors.contentTypes && <p className="text-xs text-destructive">{errors.contentTypes.message}</p>}
            </div>
          </div>
        )}

        {/* ── Step 3 — Tes outils ───────────────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-200">
            <div className="space-y-2.5">
              <Label className="text-sm font-semibold">{t.fields.platforms} *</Label>
              <PillGroup items={PLATFORMS.map(p => p[language])} value={platforms} onChange={(v) => setValue('platforms', v, { shouldValidate: true })} />
              {errors.platforms && <p className="text-xs text-destructive">{errors.platforms.message}</p>}
            </div>
            <div className="space-y-2.5">
              <Label className="text-sm font-semibold">{t.fields.languages} *</Label>
              <PillGroup items={LANGUAGES.map(l => l[language])} value={langs} onChange={(v) => setValue('languages', v, { shouldValidate: true })} />
              {errors.languages && <p className="text-xs text-destructive">{errors.languages.message}</p>}
            </div>
            <div className="space-y-2.5">
              <Label className="text-sm font-semibold">{t.fields.withWho}</Label>
              <PillGroup
                items={WITH_WHO.map(w => w[language])}
                value={specialties.filter(s => WITH_WHO.map(w => w[language].label).includes(s))}
                onChange={(selected) => {
                  const withWhoLabels = WITH_WHO.map(w => w[language].label);
                  const others = specialties.filter(s => !withWhoLabels.includes(s));
                  setValue('specialties', [...others, ...selected]);
                }}
              />
            </div>
            <div className="space-y-2.5">
              <Label className="text-sm font-semibold">{t.fields.pets}</Label>
              <PillGroup items={PETS} value={pets} onChange={(v) => setValue('pets', v)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="equipment">{t.fields.equipment}</Label>
              <Textarea id="equipment" placeholder={t.fields.equipmentPlaceholder} rows={3} {...register('equipment')} />
            </div>
          </div>
        )}

        {/* ── Step 4 — Ta vitrine ───────────────────────────────────────── */}
        {step === 4 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-200">
            {/* Photo upload */}
            <div className="space-y-2">
              <Label>{t.fields.photo}</Label>
              <div className="flex items-center gap-4">
                {photoUrl ? (
                  <div className="relative w-20 h-20 shrink-0">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={photoUrl} alt="Photo" className="w-20 h-20 rounded-full object-cover border-2 border-primary" />
                    <button
                      type="button"
                      onClick={() => { setPhotoUrl(null); setValue('photoUrl', '', { shouldValidate: true }); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center shadow"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoUploading}
                    className="w-20 h-20 rounded-full border-2 border-dashed border-border flex flex-col items-center justify-center shrink-0 hover:border-primary/50 hover:bg-primary/5 transition-colors disabled:opacity-50"
                  >
                    <Camera className="h-5 w-5 text-muted-foreground mb-0.5" />
                    <span className="text-[10px] text-muted-foreground">{photoUploading ? '...' : 'Upload'}</span>
                  </button>
                )}
                <div className="text-sm text-muted-foreground">
                  <p>{language === 'fr' ? 'JPG, PNG ou WebP · max 5 Mo' : 'JPG, PNG or WebP · max 5 MB'}</p>
                  {photoUploading && <p className="text-primary animate-pulse mt-1">{t.fields.photoUploading}</p>}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  onChange={handlePhotoChange}
                />
              </div>
              {isSubmitted && errors.photoUrl && <p className="text-xs text-destructive">{language === 'fr' ? 'Une photo de profil est requise' : 'A profile photo is required'}</p>}
            </div>

            {/* Tarifs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="videoRate">{t.fields.videoRate}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="videoRate"
                    type="number"
                    min="0"
                    step="1"
                    placeholder={t.fields.videoRatePlaceholder}
                    className="max-w-[120px]"
                    {...register('videoRate', { setValueAs: (v: string) => v === '' ? undefined : parseFloat(v) })}
                  />
                  <span className="text-sm text-muted-foreground">{t.fields.videoRateSuffix}</span>
                </div>
                {isSubmitted && errors.videoRate && <p className="text-xs text-destructive">{errors.videoRate.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hookRate">{t.fields.hookRate}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="hookRate"
                    type="number"
                    min="0"
                    step="1"
                    placeholder={t.fields.hookRatePlaceholder}
                    className="max-w-[120px]"
                    {...register('hookRate', { setValueAs: (v: string) => v === '' ? undefined : parseFloat(v) })}
                  />
                  <span className="text-sm text-muted-foreground">{t.fields.hookRateSuffix}</span>
                </div>
                {isSubmitted && errors.hookRate && <p className="text-xs text-destructive">{errors.hookRate.message}</p>}
              </div>
            </div>

            {/* Portfolio */}
            <div className="space-y-1.5">
              <Label htmlFor="website">{t.fields.portfolio} *</Label>
              <Input id="website" type="url" placeholder={t.fields.portfolioPlaceholder} {...register('website')} />
              {isSubmitted && errors.website && <p className="text-xs text-destructive">{errors.website.message}</p>}
            </div>

            {/* Réseaux */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="instagram">{t.fields.instagram}</Label>
                <Input id="instagram" placeholder="@username" {...register('instagram')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tiktok">{t.fields.tiktok}</Label>
                <Input id="tiktok" placeholder="@username" {...register('tiktok')} />
              </div>
            </div>

            {/* Vidéo phare */}
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Film className="h-4 w-4 text-violet-500" />
                  {t.prideVideo.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.prideVideo.desc}</p>
              </div>
              {/* Consent first */}
              <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20 p-3.5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    id="prideVideoConsent"
                    checked={prideVideoConsent}
                    onCheckedChange={(checked) => setPrideVideoConsent(!!checked)}
                    className="mt-0.5 shrink-0"
                  />
                  <span className="text-xs text-foreground leading-relaxed">{t.prideVideo.consentLabel}</span>
                </label>
              </div>
              {/* Upload zone */}
              {prideVideoUrl ? (
                <div className="space-y-2">
                  <video src={prideVideoUrl} controls className="w-full rounded-xl border bg-black aspect-video object-contain" />
                  <button
                    type="button"
                    onClick={() => setPrideVideoUrl('')}
                    className="flex items-center gap-1.5 text-xs font-medium text-destructive hover:text-destructive/80 transition-colors"
                  >
                    <X className="h-3.5 w-3.5" /> {t.prideVideo.remove}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={!prideVideoConsent || prideVideoUploading}
                    onClick={() => prideVideoConsent && !prideVideoUploading && videoInputRef.current?.click()}
                    className={`w-full rounded-xl border-2 border-dashed p-6 flex flex-col items-center justify-center gap-2 transition-all ${
                      !prideVideoConsent
                        ? 'opacity-40 cursor-not-allowed border-border'
                        : 'cursor-pointer border-violet-300 hover:border-violet-500 hover:bg-violet-50/50 dark:hover:bg-violet-950/20'
                    }`}
                  >
                    {prideVideoUploading
                      ? <div className="h-7 w-7 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                      : <Film className="h-7 w-7 text-violet-400" />}
                    <p className="text-sm font-medium text-muted-foreground">
                      {prideVideoUploading ? t.prideVideo.uploading : !prideVideoConsent ? t.prideVideo.consentRequired : t.prideVideo.uploadBtn}
                    </p>
                    {prideVideoConsent && !prideVideoUploading && (
                      <p className="text-xs text-muted-foreground">{t.prideVideo.uploadHint}</p>
                    )}
                  </button>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm"
                    className="sr-only"
                    onChange={handleVideoChange}
                  />
                </>
              )}
            </div>

            {/* Catégorie vidéo phare */}
            {prideVideoConsent && (
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <Star className="h-4 w-4 text-violet-500" />
                    {t.prideVideo.categoryLabel}
                    {prideVideoUrl && <span className="text-red-500 text-xs font-normal">*</span>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.prideVideo.categoryHint}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {PRIDE_CATEGORIES.map((cat) => {
                    const active = prideVideoCategory === cat.value;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => { setPrideVideoCategory(active ? '' : cat.value); setPrideVideoCategoryError(false); }}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                          active
                            ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                            : 'bg-background border-border text-muted-foreground hover:border-violet-300 hover:text-violet-600'
                        }`}
                      >
                        {cat.display}
                      </button>
                    );
                  })}
                </div>
                {prideVideoCategoryError && (
                  <p className="text-xs text-destructive font-medium">
                    {language === 'fr'
                      ? 'La catégorie est obligatoire si tu uploades une vidéo phare.'
                      : 'Category is required when uploading a pride video.'}
                  </p>
                )}
              </div>
            )}

            {/* Compte créateur */}
            <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 space-y-4">
              <div>
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4 text-indigo-500" />
                  {t.fields.accountSection}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.fields.accountSectionSub}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password">{t.fields.password}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t.fields.passwordPlaceholder}
                      autoComplete="new-password"
                      className="pr-10"
                      {...register('password')}
                    />
                    <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {isSubmitted && errors.password && (
                    <p className="text-xs text-destructive">{errors.password.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword">{t.fields.confirmPassword}</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder={t.fields.passwordPlaceholder}
                      autoComplete="new-password"
                      className="pr-10"
                      {...register('confirmPassword')}
                    />
                    <button type="button" tabIndex={-1} onClick={() => setShowConfirmPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {isSubmitted && errors.confirmPassword && (
                    <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* RGPD */}
            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="gdprConsent"
                  checked={watch('gdprConsent') ?? false}
                  onCheckedChange={(checked) => setValue('gdprConsent', !!checked, { shouldValidate: true })}
                  className="mt-0.5"
                />
                <Label htmlFor="gdprConsent" className="text-sm leading-relaxed cursor-pointer">
                  {t.gdpr.label}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground pl-7">{t.gdpr.rights}</p>
              {isSubmitted && errors.gdprConsent && (
                <p className="text-xs text-destructive pl-7">{t.gdpr.error}</p>
              )}
            </div>

            {pendingUpdatePayload && (
              <>
                {/* EMAIL_EXISTS — compte existant : inviter à se connecter */}
                {existingHasAccount === true && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">👋</span>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200">{t.updateConfirm.hasAccountMessage}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button type="button" size="sm" asChild>
                        <a href="/creator-portal/login">{t.updateConfirm.hasAccountLogin}</a>
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => { setPendingUpdatePayload(null); setExistingHasAccount(null); setPendingDuplicateType(null); }}>
                        {t.updateConfirm.cancel}
                      </Button>
                    </div>
                  </div>
                )}

                {/* EMAIL_EXISTS — pas de compte : inviter à créer + forceUpdate */}
                {existingHasAccount === false && (
                  <div className="rounded-xl border border-violet-200 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/30 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">✨</span>
                      <p className="text-sm font-medium text-violet-900 dark:text-violet-200">
                        {pendingDuplicateType === 'phone' ? t.updateConfirm.noAccountMessagePhone : t.updateConfirm.noAccountMessage}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button type="button" size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={confirmUpdate}>
                        {t.updateConfirm.noAccountConfirm}
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => { setPendingUpdatePayload(null); setExistingHasAccount(null); setPendingDuplicateType(null); }}>
                        {t.updateConfirm.cancel}
                      </Button>
                    </div>
                  </div>
                )}

                {/* PHONE_EXISTS — message générique */}
                {existingHasAccount === null && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-4 space-y-3">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">⚠️ {t.updateConfirm.message}</p>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={confirmUpdate}>
                        {t.updateConfirm.confirm}
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => { setPendingUpdatePayload(null); setExistingHasAccount(null); setPendingDuplicateType(null); }}>
                        {t.updateConfirm.cancel}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {serverError && (
              <p className="text-sm text-destructive bg-destructive/10 px-4 py-2 rounded-md">{serverError}</p>
            )}
          </div>
        )}

        {/* Navigation — masquée quand le dialog doublon est visible */}
        <div className={`flex flex-wrap gap-3 mt-6 pt-6 border-t ${pendingUpdatePayload ? 'hidden' : ''}`}>
          {step > 1 && (
            <Button type="button" variant="outline" onClick={goPrev}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              {t.prev}
            </Button>
          )}
          {step < 4 ? (
            <Button
              type="button"
              onClick={goNext}
              className="ml-auto bg-gradient-to-r from-violet-600 to-primary hover:from-violet-700 hover:to-primary/90 shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
            >
              {t.next}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button type="button" size="lg" className="w-full sm:w-auto sm:ml-auto" disabled={isSubmitting} onClick={() => handleSubmit(onSubmit)()}>
              <Sparkles className="h-4 w-4 mr-2" />
              {isSubmitting ? t.submitting : t.submit}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type View = 'landing' | 'form' | 'success';

export default function CreatorApplication() {
  const { toast } = useToast();
  const [language, setLanguage] = useState<'fr' | 'en'>('fr');
  const [view, setView] = useState<View>('landing');
  const [submittedEmail, setSubmittedEmail] = useState<string>('');
  const [showCguModal, setShowCguModal] = useState(false);
  const [cguTemplate, setCguTemplate] = useState<ContractTemplate | null>(null);

  const t = content[language];

  useEffect(() => {
    fetchContractTemplate('cgu_creator', language).then(setCguTemplate);
  }, [language]);

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12">
          <div className="max-w-4xl w-full">
            <div className="flex justify-center mb-10">
              <LanguageSwitcher language={language} onChange={setLanguage} />
            </div>

            <div className="text-center mb-12">
              <Badge variant="secondary" className="mb-6">
                <Video className="h-3.5 w-3.5 mr-1.5" />
                {t.hero.badge}
              </Badge>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                {t.hero.title}
              </h1>

              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                {t.hero.subtitle}
              </p>

              <Button size="lg" onClick={() => setView('form')}>
                {t.cta}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {t.steps.map((step, i) => (
                <Card key={i}>
                  <CardContent className="p-6 text-center">
                    <step.icon className="h-8 w-8 mx-auto mb-3 text-primary" />
                    <h3 className="font-semibold mb-1">{step.label}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {t.benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-card/50">
                  <benefit.icon className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-medium mb-1">{benefit.title}</h4>
                    <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-background to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-6">
          {/* Emoji burst */}
          <div className="flex justify-center gap-3 text-3xl animate-bounce">
            <span>🎉</span><span>⭐</span><span>🚀</span>
          </div>

          {/* Trophy */}
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto shadow-lg shadow-orange-200 dark:shadow-orange-900/40">
            <Trophy className="h-12 w-12 text-white" />
          </div>

          <div>
            <h2 className="text-3xl font-bold mb-3">{t.success.title}</h2>
            <p className="text-muted-foreground">{t.success.message}</p>
          </div>

          {/* XP awarded */}
          <div className="rounded-xl border border-primary/20 bg-primary/5 px-5 py-4 space-y-2">
            <p className="text-sm font-semibold text-primary flex items-center justify-center gap-2">
              <Zap className="h-4 w-4" /> Profil créé avec succès
            </p>
            <div className="h-2 bg-primary/10 rounded-full overflow-hidden">
              <div className="h-full w-full bg-gradient-to-r from-violet-500 to-primary rounded-full animate-pulse" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/creator-portal/login">
              <Button size="lg" className="rounded-xl w-full sm:w-auto">
                {language === 'fr' ? 'Accéder à mon portail' : 'Access my portal'}
              </Button>
            </a>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setView('landing')}
              className="rounded-xl"
            >
              {t.success.back}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // view === 'form'
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <h2 className="text-2xl font-bold">{t.form.title}</h2>
            <LanguageSwitcher language={language} onChange={setLanguage} />
          </div>

          <div className="bg-card border rounded-2xl p-6 sm:p-8 shadow-sm">
            <ApplicationForm
              language={language}
              onSuccess={(email) => {
                setSubmittedEmail(email);
                if (cguTemplate) {
                  setShowCguModal(true);
                } else {
                  setView('success');
                }
              }}
            />
          </div>

          <ContractAcceptanceModal
            isOpen={showCguModal}
            onClose={() => {
              setShowCguModal(false);
              setView('success');
            }}
            template={cguTemplate}
            onAccept={async () => {
              if (!cguTemplate) return;
              await acceptContractTemplate(cguTemplate.id);
              setShowCguModal(false);
              setView('success');
              toast({
                title: language === 'fr' ? 'CGU acceptées' : 'Terms accepted',
                description:
                  language === 'fr'
                    ? "Les Conditions Générales d'Utilisation ont été acceptées."
                    : 'Terms and conditions have been accepted.',
              });
            }}
            title={
              language === 'fr'
                ? "Conditions Générales d'Utilisation"
                : 'Terms and Conditions'
            }
            description={
              language === 'fr'
                ? "Veuillez lire et accepter nos CGU pour finaliser votre candidature."
                : 'Please read and accept our terms to complete your application.'
            }
          />
        </div>
      </div>
    </div>
  );
}
